import React, { Fragment, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { AppDispatch } from 'state/store';
import { getAllFeatureFlags } from 'services/feature-flags.service';
import { getFlags, getError as getFlagsError, setError as setFlagsError } from 'state/reducers/feature-flags.reducer';
import { getAllSystemSettings } from 'services/system-settings.service';
import {
	getSettings,
	getError as getSystemSettingsError,
	setError as setSettingsError,
} from 'state/reducers/system-settings.reducer';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { useAppSelector } from 'hooks/redux.hooks';
import { Page } from 'theme/elements/Page';
import { Box, Stack, Tab, Typography } from '@mui/material';
import { Tabs } from '@mui/material';
import { FeatureFlags } from 'sections/SystemSettings/FeatureFlags';
import { GeneralSettings } from 'sections/SystemSettings/GeneralSettings';
import { TabPanel } from 'sections/NewAccounts/Pending';
import { systemSettingsSubtitlesMapping, systemSettingsTypes } from 'constants/system-settings.constants';
import { WarningDialog } from 'components/WarningDialog';
import { DialogBox } from 'components/DialogBox';
import { initialMessage } from 'constants/ui.constants';
import { hasPermission } from 'components/AuthorizationComponent';
import { permissionEnums } from 'constants/permission.constants';

const boxStyle = {
	maxWidth: 910,
	width: '100%',
	whiteSpace: 'pre-line',
	wordBreak: 'break-word',
	lineHeight: 1.6,
};

const SystemSettings: React.FC = () => {
	const dispatch: AppDispatch = useDispatch();
	const flags = useAppSelector(getFlags);
	const settings = useAppSelector(getSettings);
	const flagsError = useAppSelector(getFlagsError);
	const systemSettingsError = useAppSelector(getSystemSettingsError);
	const t = useAppSelector(selectTranslations);
	const prefix = 'page.system_settings.';
	const [tab, setTab] = useState(0);
	const editPermissions = hasPermission([permissionEnums.EDIT_SYSTEM_SETTINGS]);
	const [confirmationDialogConfig, setConfirmationDialogConfig] = useState({
		isOpen: false,
		title: '',
		message: '',
		onConfirm: () => {},
		onCancel: () => {},
	});

	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		setTab(newValue);
	};
	// Dialog handlers
	const requestChangeConfirmation = (settingName: string, onConfirm: () => void, onCancel: () => void) => {
		const message = t(`${prefix}approveQuestionSubtitle`).replace('%%settingName%%', settingName);
		setConfirmationDialogConfig({
			isOpen: true,
			title: t(`${prefix}approveQuestionTitle`),
			message,
			onConfirm,
			onCancel,
		});
	};

	const handleConfirmationDialogClose = () => {
		confirmationDialogConfig.onCancel();
		setConfirmationDialogConfig({
			isOpen: false,
			title: '',
			message: '',
			onConfirm: () => {},
			onCancel: () => {},
		});
	};

	const handleConfirmationDialogApprove = () => {
		confirmationDialogConfig.onConfirm();
		setConfirmationDialogConfig({
			isOpen: false,
			title: '',
			message: '',
			onConfirm: () => {},
			onCancel: () => {},
		});
	};

	// Error dialog handlers
	const errorDialogOpen = useMemo(() => {
		return flagsError.exist || systemSettingsError.exist;
	}, [flagsError.exist, systemSettingsError.exist]);

	const errorDialogMessage = useMemo(() => {
		if (flagsError.exist && flagsError.text) {
			return flagsError.text;
		}
		if (systemSettingsError.exist && systemSettingsError.text) {
			return systemSettingsError.text;
		}
		return '';
	}, [flagsError.exist, flagsError.text, systemSettingsError.exist, systemSettingsError.text]);

	const handleErrorDialogClose = () => {
		dispatch(setFlagsError(initialMessage));
		dispatch(setSettingsError(initialMessage));
	};

	useEffect(() => {
		Promise.all([dispatch(getAllFeatureFlags()), dispatch(getAllSystemSettings())]);
	}, [dispatch]);

	const tabTypes = useMemo(() => {
		if (settings.length > 0) {
			return Object.keys(_.groupBy(settings, 'domain'));
		}
		return [];
	}, [settings]);

	const tabsConfig = useMemo(() => {
		if (tabTypes.length > 0) {
			return {
				feature_flags: {
					component: FeatureFlags,
					subtitle: systemSettingsSubtitlesMapping[systemSettingsTypes.FEATURE_FLAGS].subtitle,
					warning: systemSettingsSubtitlesMapping[systemSettingsTypes.FEATURE_FLAGS].warning,
					dataTransform: (data: any) => data,
				},
				...tabTypes.reduce(
					(acc: any, type: string) => ({
						...acc,
						[type]: {
							component: GeneralSettings,
							subtitle: systemSettingsSubtitlesMapping[type]?.subtitle,
							warning: systemSettingsSubtitlesMapping[type]?.warning,
							dataTransform: (data: any) => _.orderBy(data, 'type', 'asc'),
						},
					}),
					{}
				),
			};
		}
		return {};
	}, [flags, settings]);

	// Helper function to get data for each tab
	const getTabData = useMemo(() => {
		const tabDataCache: Record<string, any> = {};

		Object.keys(tabsConfig).forEach((tabType) => {
			const config = tabsConfig[tabType];
			const rawData =
				tabType === 'feature_flags' ? flags : settings.filter((setting: any) => setting.domain === tabType);
			tabDataCache[tabType] = config.dataTransform(rawData);
		});

		return (tabType: string) => tabDataCache[tabType] || [];
	}, [tabsConfig, flags, settings]);

	return (
		<Page>
			<Fragment>
				<WarningDialog
					open={errorDialogOpen}
					subTitle={t(errorDialogMessage)}
					handleCancel={handleErrorDialogClose}
				/>
				<DialogBox
					openDialog={confirmationDialogConfig.isOpen}
					handleClose={handleConfirmationDialogClose}
					handleApprove={handleConfirmationDialogApprove}
					handleReject={handleConfirmationDialogClose}
					title={confirmationDialogConfig.title}
				>
					<Typography>{confirmationDialogConfig.message}</Typography>
				</DialogBox>
				<Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
					<Tabs value={tab} onChange={handleTabChange} aria-label="system settings tabs">
						{Object.keys(tabsConfig).map((type) => (
							<Tab key={type} label={t(`${prefix}tab.${type}.title`)} />
						))}
					</Tabs>
				</Box>

				<>
					{Object.keys(tabsConfig).map((tabType: string, index: number) => {
						const config = tabsConfig[tabType];
						const Component = config.component;
						const data = getTabData(tabType);

						return (
							<TabPanel value={tab} index={index} key={tabType}>
								<Stack spacing={2} sx={{ marginBottom: '20px' }}>
									<Typography variant="pageTitle">{t(`${prefix}${tabType}.table.title`)}</Typography>
									<Box sx={boxStyle}>
										<Typography variant="boldBody1" sx={{ mb: 1 }}>
											{config.subtitle}
										</Typography>
										<Typography variant="body2" color="error.main">
											{config.warning}
										</Typography>
									</Box>
									<Component
										data={data}
										prefix={`${prefix}${tabType}`}
										editPermissions={editPermissions}
										requestChangeConfirmation={requestChangeConfirmation}
									/>
								</Stack>
							</TabPanel>
						);
					})}
				</>
			</Fragment>
		</Page>
	);
};

export default SystemSettings;
